import React, { useState, useCallback, useRef } from 'react';
import { CustomInputProps } from '../../../../types/summarizer';
import { useSummarizer, getSourceTypeFromFile } from '../../../../core/store/useSummarizer';
import { useDropzone } from 'react-dropzone';
import { HiOutlineDocumentAdd } from 'react-icons/hi';
import { FaFilePdf, FaFileWord, FaFileAlt } from 'react-icons/fa';
import { IoMdClose } from 'react-icons/io';
import { FileUp } from 'lucide-react';

import { Button } from '../../../ui/Button';
import { ProcessingButton } from '../../../ui/ProcessingButton';
import { Label } from '../../../ui/label';
import { Slider } from '../../../ui/slider';
import { TextArea, ProgressBar, SummaryOptionsBar } from '@components/ui';
import { SOURCE_TYPES } from '../../../../constants/summarizer';

const ContentLength = ({
  summaryLength,
  setSummaryLength,
}: {
  summaryLength: number[];
  setSummaryLength: (value: number[]) => void;
}) => {
  return (
    <div className="flex items-center gap-3">
      <Label className="text-sm font-medium text-gray-700">Summary Length:</Label>
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500">Short</span>
        <div className="w-20">
          <Slider
            value={summaryLength}
            onValueChange={setSummaryLength}
            max={100}
            min={0}
            step={1}
            className="w-full"
          />
        </div>
        <span className="text-xs text-gray-500">Long</span>
      </div>
    </div>
  );
};

const TextSummarizerInput: React.FC<CustomInputProps> = ({ config }) => {
  const {
    textInput,
    textError,
    selectedFile,
    uploadProgress,
    uploadedUrl,
    isUploading,
    tone,
    format,
    language,
    setTextInput,
    setSelectedFile,
    validateFile,
    clearError,
    setTone,
    summaryType,
    setSummaryType,
    uploadFile,
    summarize,
    isLoading: storeLoading,
    setError,
  } = useSummarizer();

  const [summaryLength, setSummaryLength] = useState([50]);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const maxLength = config.inputs?.text?.maxLength || 50000;
  const minLength = config.inputs?.text?.minLength || 50;

  const validateText = (text: string) => {
    return text.length >= minLength && text.length <= maxLength;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName?.split('.').pop()?.toLowerCase() || '';
    if (extension === 'pdf') return <FaFilePdf className="text-red-500 text-2xl" />;
    if (['doc', 'docx'].includes(extension))
      return <FaFileWord className="text-blue-500 text-2xl" />;
    return <FaFileAlt className="text-gray-500 text-2xl" />;
  };

  const handleSubmit = async () => {
    if (selectedFile && uploadedUrl) {
      try {
        const sourceType = getSourceTypeFromFile(selectedFile.name);
        await summarize({
          link_or_id: uploadedUrl,
          source: sourceType,
          title: selectedFile.name.replace(/\.[^/.]+$/, ''),
          extension: selectedFile.name?.split('.').pop(),
          tone,
          format,
          language,
        });
      } catch (error) {
        console.error('Summarization failed:', error);
      }
      return;
    }

    if (!textInput.trim()) return;

    const isValid = validateText(textInput);
    if (!isValid) return;

    try {
      await summarize({
        text: textInput.trim(),
        source: SOURCE_TYPES.TEXT,
        tone,
        format,
        language,
      });
    } catch (error) {
      console.error('Summarization failed:', error);
    }
  };

  const handleUploadDoc = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      const { isValid, error } = validateFile(file, {
        acceptedTypes: ['.pdf', '.doc', '.docx', '.txt'],
        maxSize: 50 * 1024 * 1024, // 50MB
      });

      if (!isValid) {
        setError(error || 'File validation failed');
      }

      setSelectedFile(file);
      setShowFileUpload(true);
      setTextInput('');

      uploadFile(file).catch((error: Error) => {
        console.error('Upload failed:', error);
      });
    },
    [validateFile, setSelectedFile, uploadFile, setTextInput]
  );

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(Array.from(files));
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setShowFileUpload(false);
    clearError();
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileSelect,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
    },
    maxFiles: 1,
    disabled: storeLoading || isUploading,
    noClick: true,
    noKeyboard: true,
  });

  const characterCount = textInput.length;
  const isValidLength = characterCount >= minLength && characterCount <= maxLength;
  const hasFile = selectedFile && uploadProgress === 100;
  const canSubmit =
    (textInput.trim() && isValidLength && !textError && !storeLoading) ||
    (hasFile && !isUploading && !storeLoading);
  const loading = storeLoading || isUploading;

  return (
    <>
      <div className="flex flex-wrap items-center gap-4 py-3 border-b z-10">
        <SummaryOptionsBar
          summaryType={summaryType}
          tone={tone}
          onSummaryTypeChange={setSummaryType}
          onToneChange={setTone}
          disabled={loading}
          className="flex-1"
          gap="md"
        />

        {false && (
          <ContentLength summaryLength={summaryLength} setSummaryLength={setSummaryLength} />
        )}
      </div>

      <div className="relative h-full">
        {showFileUpload && selectedFile ? (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 rounded-lg p-8 mb-5">
            {getFileIcon(selectedFile.name)}
            <p className="text-lg font-medium mt-4">{selectedFile.name}</p>
            <p className="text-sm text-gray-500 mt-1">{formatFileSize(selectedFile.size)}</p>

            {isUploading && (
              <div className="w-full space-y-3 max-w-xs mt-4">
                <ProgressBar value={uploadProgress} className="h-2" />
                <p className="text-xs text-gray-500 text-center">Uploading... {uploadProgress}%</p>
              </div>
            )}

            {uploadProgress === 100 && !isUploading && (
              <p className="text-sm text-green-600 mt-4">✓ File uploaded successfully</p>
            )}

            <Button
              onClick={handleRemoveFile}
              className="mt-4 !flex items-center gap-2 text-red-600 hover:text-red-700 !bg-white"
            >
              <IoMdClose className="text-lg" />
              Remove file
            </Button>
          </div>
        ) : (
          <div {...getRootProps()} className="w-full h-full relative">
            <input {...getInputProps()} />
            <TextArea
              value={textInput}
              onChange={(e) => {
                setTextInput(e.target.value);
                if (e.target.value && selectedFile) {
                  handleRemoveFile();
                }
              }}
              placeholder={
                isDragActive
                  ? 'Drop your file here...'
                  : config.inputs?.text?.placeholder ||
                    'Enter or paste your text, or drag a file here'
              }
              disabled={loading}
              rows={config.inputs?.text?.rows || 12}
              className={`w-full h-full py-4 !outline-none !border-none !resize-none focus:outline-none focus:ring-0 text-gray-900 ${
                textError || !isValidLength
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              } ${loading ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'} ${
                isDragActive ? 'bg-blue-50 border-blue-400' : ''
              }`}
              style={{
                minHeight: '300px',
                fontSize: '16px',
                lineHeight: '1.5',
              }}
            />

            {!isDragActive && (
              <div className="absolute bottom-3 right-3 text-xs text-gray-500 bg-white px-2 py-1 rounded-lg">
                {characterCount} / {maxLength}
              </div>
            )}

            {isDragActive && (
              <div className="absolute inset-0 bg-blue-50 bg-opacity-90 flex items-center justify-center pointer-events-none">
                <div className="text-center">
                  <FileUp className="w-12 h-12 text-blue-500 mx-auto mb-2" />
                  <p className="text-lg font-medium text-blue-700">
                    {config.inputs.file?.placeholder || 'Drop your file here'}
                  </p>
                  <p className="text-sm text-blue-600 mt-1">
                    {config.inputs.file?.helpText || 'PDF, DOC, DOCX, or TXT files up to 50MB'}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.txt"
          onChange={handleFileInputChange}
          className="hidden"
        />
      </div>

      <div className="flex items-center justify-between w-full gap-3 mt-auto">
        <Button
          onClick={handleUploadDoc}
          className="text-gray-700 bg-white hover:!bg-gray-100 hover:text-black hover:!border-none hover:!outline-none active:border-none active:outline-none active:bg-gray-100 flex items-center gap-2 text-sm"
        >
          <HiOutlineDocumentAdd className="text-lg" />
          <span>Upload Doc</span>
        </Button>
        <ProcessingButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          isLoading={loading}
          submitButtonText="Summarize"
        />
      </div>

      {textInput.trim() && !isValidLength && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm">
          {characterCount < minLength
            ? `Text is too short. Please enter at least ${minLength} characters.`
            : `Text is too long. Please reduce to ${maxLength} characters or less.`}
        </div>
      )}
    </>
  );
};

export default TextSummarizerInput;
